import { useForm, usePage } from '@inertiajs/react'
import { Button, NativeSelect, Paper, Stack, Text } from '@mantine/core'
import { FormEvent, useState } from 'react'
import type { ProjectProps, SharedProps, SkillProps } from '@/types'

type ProjectModeProps = {
  mode: 'project'
  availableSkills: SkillProps[]
  assignableType: 'Project'
  assignableId: string
}

type FullModeProps = {
  mode: 'full'
  allSkills: SkillProps[]
  projects: ProjectProps[]
}

type Props = ProjectModeProps | FullModeProps

export default function AssignSkillForm(props: Props) {
  const { csrf_token } = usePage<SharedProps>().props
  const [open, setOpen] = useState(false)

  const { data, setData, post, processing, reset } = useForm({
    skill_id: '',
    assignable_type: props.mode === 'project' ? props.assignableType : 'User',
    assignable_id: props.mode === 'project' ? props.assignableId : '',
    enabled: true,
    auto_activate: false,
    authenticity_token: csrf_token || '',
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(`/skill_assignments`, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        setOpen(false)
      },
    })
  }

  const skills = props.mode === 'project' ? props.availableSkills : props.allSkills

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        variant="light"
        justify="flex-start"
      >
        + Assign Skill
      </Button>
    )
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} p="lg">
      <Stack gap="md">
        <NativeSelect
          id="assign-skill"
          label="Skill"
          value={data.skill_id}
          onChange={(e) => setData('skill_id', e.target.value)}
          data={[
            { value: '', label: 'Select a skill...' },
            ...skills.map((skill) => ({ value: skill.id, label: skill.name })),
          ]}
        />

        {props.mode === 'full' ? (
          <NativeSelect
            id="assign-target"
            label="Assign to"
            value={data.assignable_type === 'User' ? 'User' : data.assignable_id}
            onChange={(e) => {
              if (e.target.value === 'User') {
                setData({ ...data, assignable_type: 'User', assignable_id: '' })
              } else {
                setData({ ...data, assignable_type: 'Project', assignable_id: e.target.value })
              }
            }}
            data={[
              { value: 'User', label: 'User-wide' },
              ...props.projects.map((project) => ({ value: project.id, label: project.name })),
            ]}
          />
        ) : null}

        <Text c="dimmed" size="sm">
          Assign a skill now, then refine activation behavior in a later UI pass.
        </Text>

        <Button.Group>
          <Button type="submit" disabled={processing || !data.skill_id}>
            {processing ? 'Assigning...' : 'Assign'}
          </Button>
          <Button type="button" variant="default" onClick={() => { reset(); setOpen(false) }}>
            Cancel
          </Button>
        </Button.Group>
      </Stack>
    </Paper>
  )
}
